import { Button } from "@/atoms-react/button/Button.react";
import Column from "@/atoms-react/column/Column.react";
import DotsNavigation from "@/atoms-react/dots-navigation/DotsNavigation.react";
import type { LayoutBaseProps } from "@/atoms-react/layout-base/LayoutBase.react";
import type { DeclarativeMonthlyIncome } from "@/features/flow/types";
import { ChevronRightIcon } from "@heroicons/react/20/solid";
import { useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import { FlowHeader } from "@/flow/components/flow-header.react";

import { RadioCard } from "@/atoms-react/radio-card/index.react.tsx";
import { Base } from "@/atoms-react/text/Text.react.tsx";
import { useOrigin } from "@/features/flow/hooks/use-origin";
import { useAppDispatch } from "@/redux/hooks";
import { setIsInPreviousStep } from "@/redux/slices/flowSlice";
import { isDeclarativeMonthlyIncomeValid, monthlyIncomes } from "@/utils/declarative.utils";
import { twMerge } from "tailwind-merge";
interface DeclarativeIdentityMonthlyIncomeProps extends LayoutBaseProps {
  onValidated?: (output: DeclarativeIdentityMonthlyIncomeOutput) => void;
  declarativeMonthlyIncome?: DeclarativeMonthlyIncome;
}

export type DeclarativeIdentityMonthlyIncomeOutput = DeclarativeMonthlyIncome;

function DeclarativeIdentityMonthlyIncome({
  onValidated = () => {},
  declarativeMonthlyIncome
}: DeclarativeIdentityMonthlyIncomeProps) {
  const dispatch = useAppDispatch();
  const { t } = useTranslation(["pass"]);
  const [data, setData] = useState<DeclarativeMonthlyIncome>({
    trans_key: declarativeMonthlyIncome?.trans_key as string,
    maximum: declarativeMonthlyIncome?.maximum ?? null,
    minimum: declarativeMonthlyIncome?.minimum ?? null
  });
  const origin = useOrigin();
  function execute() {
    if (!isDeclarativeMonthlyIncomeValid(data)) {
      toast.error(t("flow.id_control.error.must_enter_fields"));
      return;
    }
    dispatch(setIsInPreviousStep(false));
    onValidated(data);
  }

  return (
    <>
      <Column>
        <div className="mb-12 grow">
          <FlowHeader
            title={`${t(
              "olkypass.mainPage.olkypass.modalOpenOlkyPay.steps.monthly_incomes.question"
            )} *`}
            description=""
            onPreviousClick={() => dispatch(setIsInPreviousStep(true))}
          />
          <div className="my-4 mt-4 grid grid-cols-1 gap-2 md:grid-cols-2">
            {monthlyIncomes.map((m) => (
              <RadioCard
                active={data?.trans_key === m.trans_key}
                key={m.trans_key}
                onClick={() => setData(m)}
                origin={origin}
              >
                <Base
                  className={twMerge(data?.trans_key === m.trans_key && "text-sm dark:text-black")}
                >
                  {t(
                    "olkypass.mainPage.olkypass.modalOpenOlkyPay.steps.monthly_incomes.responses." +
                      m.trans_key
                  )}
                </Base>
              </RadioCard>
            ))}
          </div>
        </div>
        <div className="flex shrink-0 flex-col items-center">
          <DotsNavigation total={8} current={6} />
          <Button
            shortcut="enter"
            onClick={() => execute()}
            icon={<ChevronRightIcon />}
            className="mt-4"
          >
            {t("flow.id_control.input.continue")}
          </Button>
        </div>
      </Column>
    </>
  );
}

export default DeclarativeIdentityMonthlyIncome;
